// Configuración de Firebase Client SDK
// Este archivo configura Firebase para el frontend

import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';

// Configuración de Firebase - obtiene las credenciales desde Firebase Console
const firebaseConfig = {
  apiKey: "AIzaSyA4NVUClh0WnmCtxeaqNwA5nuuhy-d3084",
  authDomain: "micropos-latam.firebaseapp.com",
  projectId: "micropos-latam",
  storageBucket: "micropos-latam.firebasestorage.app",
  messagingSenderId: "547969675473",
  appId: "1:547969675473:web:72a518ee0c5638b61d55f0",
  measurementId: "G-TZD6G1MTKG"
};

// Inicializar Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// Proveedor de Google
const googleProvider = new GoogleAuthProvider();

// Agregar dominio autorizado para Google OAuth
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

/**
 * Iniciar sesión con correo y contraseña
 */
export const loginWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const idToken = await userCredential.user.getIdToken();
    
    return {
      success: true,
      user: {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        displayName: userCredential.user.displayName,
        photoURL: userCredential.user.photoURL
      },
      idToken
    };
  } catch (error) {
    const errorMessage = translateAuthError(error.code);
    return {
      success: false,
      error: errorMessage,
      code: error.code
    };
  }
};

/**
 * Iniciar sesión con Google (Popup)
 */
export const loginWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const idToken = await result.user.getIdToken();
    
    return {
      success: true,
      user: {
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL
      },
      idToken
    };
  } catch (error) {
    // Si el usuario cerró el popup
    if (error.code === 'auth/popup-closed-by-user') {
      return {
        success: false,
        error: 'La ventana de inicio de sesión fue cerrada antes de completar la autenticación',
        code: error.code
      };
    }
    
    const errorMessage = translateAuthError(error.code);
    return {
      success: false,
      error: errorMessage,
      code: error.code
    };
  }
};

/**
 * Registrar nuevo usuario con correo y contraseña
 */
export const registerWithEmail = async (email, password, displayName) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Actualizar el perfil con el nombre
    if (displayName) {
      await updateProfile(userCredential.user, { displayName });
    }
    
    const idToken = await userCredential.user.getIdToken();
    
    return {
      success: true,
      user: {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        displayName: displayName || userCredential.user.displayName,
        photoURL: userCredential.user.photoURL
      },
      idToken
    };
  } catch (error) {
    const errorMessage = translateAuthError(error.code);
    return {
      success: false,
      error: errorMessage,
      code: error.code
    };
  }
};

/**
 * Cerrar sesión
 */
export const logoutFirebase = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: translateAuthError(error.code)
    };
  }
};

/**
 * Enviar correo de recuperación de contraseña
 */
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: translateAuthError(error.code)
    };
  }
};

/**
 * Traduce errores de Firebase Auth al español latinoamericano
 */
const translateAuthError = (errorCode) => {
  // Normalizar el código de error
  const normalizedCode = errorCode?.toLowerCase() || '';
  
  const errorTranslations = {
    // Errores comunes
    'auth/invalid-email': 'El correo electrónico no es válido',
    'auth/user-disabled': 'Esta cuenta ha sido deshabilitada',
    'auth/user-not-found': 'No existe ninguna cuenta con este correo electrónico',
    'auth/wrong-password': 'La contraseña es incorrecta',
    'auth/email-already-in-use': 'Ya existe una cuenta con este correo electrónico',
    'auth/operation-not-allowed': 'Esta operación no está permitida. Habilita el proveedor en Firebase Console',
    'auth/weak-password': 'La contraseña debe tener al menos 6 caracteres',
    'auth/invalid-credential': 'Las credenciales no son válidas. Verifica tu correo y contraseña',
    'auth/INVALID_LOGIN_CREDENTIALS': 'Correo electrónico o contraseña incorrectos',
    
    // Errores de red
    'auth/network-request-failed': 'Error de conexión. Verifica tu conexión a internet',
    'auth/too-many-requests': 'Demasiados intentos. Por favor, espera un momento e intenta nuevamente',
    
    // Errores de OAuth/Google
    'auth/popup-closed-by-user': 'La ventana de inicio de sesión fue cerrada antes de completar la autenticación',
    'auth/cancelled-popup-request': 'Solo se permite una ventana de inicio de sesión a la vez',
    'auth/auth-domain-config-required': 'Configuración de dominio de autenticación requerida',
    'auth/operation-not-supported-in-this-environment': 'Esta operación no es soportada en este entorno',
    'auth/unauthorized-domain': 'El dominio no está autorizado para OAuth',
    'auth/provider-already-linked': 'Ya tienes vinculado este método de inicio de sesión',
    'auth/no-such-provider': 'Este método de inicio de sesión no está vinculado a tu cuenta',
    
    // Errores de verificación
    'auth/invalid-verification-code': 'El código de verificación no es válido',
    'auth/invalid-verification-id': 'El ID de verificación no es válido',
    'auth/missing-verification-code': 'Falta el código de verificación',
    'auth/missing-verification-id': 'Falta el ID de verificación',
    
    // Errores de token
    'auth/user-token-expired': 'El token de sesión ha expirado. Por favor, inicia sesión nuevamente',
    'auth/requires-recent-login': 'Por seguridad, por favor cierra sesión y vuelve a iniciar',
    
    // Errores genéricos
    'auth/internal-error': 'Error interno del servidor',
    'auth/invalid-api-key': 'La clave API no es válida. Por favor, contacta al administrador',
    'auth/api-key-not-valid': 'La clave API no es válida. Por favor, contacta al administrador',
    'auth/api-key-not-valid.-please-pass-a-valid-api-key': 'La clave API no es válida. Por favor, contacta al administrador',
    'auth/app-not-authorized': 'La aplicación no está autorizada',
    'auth/argument-error': 'Parámetros inválidos',
    'auth/invalid-phone-number': 'El número de teléfono no es válido',
    'auth/invalid-continue-uri': 'La URL de continuación no es válida',
    'auth/missing-continue-uri': 'Falta la URL de continuación',
    'auth/invalid-oauth-provider': 'El proveedor OAuth no es válido',
    
    // Errores de password reset
    'auth/null-user': 'El usuario es null',
    'auth/invalid-action-code': 'El código no es válido',
    'auth/expired-action-code': 'El código ha expirado',
    'auth/missing-action-code': 'Falta el código de acción'
  };

  return errorTranslations[errorCode] || errorTranslations[normalizedCode] || `Error de autenticación: ${errorCode}`;
};

export { auth, googleProvider };
export default app;
